import React, { useEffect, useRef, useState } from 'react';
import { IKpiVessel, TVoyages, TVoyagesItem } from '../../../kpiTypes';
import { AgGridReact } from 'ag-grid-react';
import { themeBalham } from 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-community';
import VoyagesOfOriginsChart from './VoyagesOfOriginsChart';
import { formatValueAsString } from '../../../../../utils/helpers';
import VoyagesStats from './VoyagesStats';
import { useAppSelector } from '../../../../../core/rootHooks';
import { keyMetricsInfoSelector } from '../../../kpiSelectors';
import { RootState } from '../../../../../core/store';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';

export interface IVoyagesProps {
	vessel: IKpiVessel;
	vesselVoyages: TVoyages | null;
}

const Voyages: React.FC<IVoyagesProps> = ({ ...props }) => {
	const numberFormatOptions: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
	const vesselKeyMetrics = useAppSelector((state: RootState) => keyMetricsInfoSelector(state, props.vessel.imoNumber!));

	const gridColDefs: ColDef<TVoyagesItem>[] = [
		{ headerName: 'Voyage Id', field: 'voyageId' },
		{ headerName: 'Origin Port', field: 'originPort' },
		{ headerName: 'Origin Country', field: 'originCountry' },
		{ headerName: 'Origin Region', field: 'originRegion' },

		{ headerName: 'Origin Port Activity', field: 'originPortActivity' },
		{
			headerName: 'Origin Port Start Time',
			field: 'originStartTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'Origin Port End Time',
			field: 'originEndTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'Time In Origin Port',
			field: 'totalInOriginPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},

		{
			headerName: 'Origin Port Fuel Consumed',
			field: 'fuelConsumedInOriginPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Origin Port Emissions',
			field: 'emissionConsumedInOriginPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Voyage Distance',
			field: 'voyageDistance',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Voyage Time',
			field: 'voyageTime',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{ headerName: 'Voyage Status', field: 'voyageStatus' },
		{
			headerName: 'Voyage Fuel Consumption',
			field: 'fuelConsumedOnVoyage',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Voyage Emissions',
			field: 'emissionConsumedOnVoyage',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},

		{ headerName: 'Destination Port', field: 'destinationPort' },
		{ headerName: 'Destination Country', field: 'destinationCountry' },
		{ headerName: 'Destination Region', field: 'destinationRegion' },

		{ headerName: 'Destination Port Activity', field: 'destinationPortActivity' },
		{
			headerName: 'Destination Port Start Time',
			field: 'destinationStartTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'Destination Port End Time',
			field: 'destinationEndTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},

		{
			headerName: 'Time In Destination Port',
			field: 'totalInDestinationPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Destination Port Fuel Consumed',
			field: 'fuelConsumedInDestinationPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Destination Port Emissions',
			field: 'emissionConsumedInDestinationPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Total Fuel Consumed',
			field: 'totalFuelConsumed',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Total Emissions',
			field: 'totalEmissionConsumed',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
	];

	const rowData = props.vesselVoyages && props.vesselVoyages.dataList ? props.vesselVoyages.dataList : [];

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-col gap-4 w-full">
			<div className="flex flex-row gap-4 basis-1/2">
				<div className="flex flex-col w-max border-1 border-msi-darkgrey-color-300 rounded p-1">
					<VoyagesStats keyMetricsData={vesselKeyMetrics ? vesselKeyMetrics.data : null} />
				</div>
				<div className="flex flex-col w-full border border-msi-darkgrey-color-300 rounded">
					<ExpandableContainer panelOffsets={panelOffset}>
						<VoyagesOfOriginsChart imoNumber={props.vessel.imoNumber} vesselVoyages={props.vesselVoyages || null} />
					</ExpandableContainer>
				</div>
			</div>
			<div className="flex flex-col gap-4 basis-1/2 border border-msi-darkgrey-color-300 rounded p-1">
				<div className="w-full h-full">
					<ExpandableContainer panelOffsets={panelOffset}>
						<AgGridReact key={props.vessel.imoNumber} columnDefs={gridColDefs || null} rowData={rowData || null} theme={themeBalham} />
					</ExpandableContainer>
				</div>
			</div>
		</div>
	);
};

export default Voyages;
